// API utilities for Gift-Wish with caching
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Simple in-memory cache
const cache = new Map();
const CACHE_DURATION = 30000; // 30 seconds

// Cache helpers
const getCacheKey = (url, params) => `${url}:${JSON.stringify(params || {})}`;

const getFromCache = (key) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  cache.delete(key);
  return null;
};

const setCache = (key, data) => {
  cache.set(key, { data, timestamp: Date.now() });
};

// Clear cache for specific patterns (useful after mutations)
export const clearCache = (pattern) => {
  if (!pattern) {
    cache.clear();
    return;
  }
  for (const key of cache.keys()) {
    if (key.includes(pattern)) {
      cache.delete(key);
    }
  }
};

// Create axios instance with default config
const api = axios.create({
  baseURL: `${API_BASE}/api`,
  withCredentials: true,
});

// Add interceptor to include Authorization header from localStorage
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('session_token');
    }
    return Promise.reject(error);
  }
);

// Cached GET wrapper
export const cachedGet = async (url, config = {}) => {
  const cacheKey = getCacheKey(url, config.params);
  const cached = getFromCache(cacheKey);
  
  if (cached) {
    return { data: cached, fromCache: true };
  }
  
  const response = await api.get(url, config);
  setCache(cacheKey, response.data);
  return response;
};

export default api;
export { API_BASE };
